import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Landmark, ShieldCheck, ScrollText, ChevronRight, ZoomIn, Link2, QrCode, Check, FileDown } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { useMediaViewer, CiteThis, CitationList } from "@/components/shared";
import {
  Reveal,
  SectionLabel,
  ProvenanceStatusBadge,
  FramedImage,
  ItemCard,
  CategoryCard,
  ProvenanceTimeline,
  ArticleRenderer,
} from "@/components/collection";

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ==================== Client-side social meta management ==================== */
const DEFAULT_TITLE = "Elvis Aloha 1973 Collection";

const upsertMeta = (selector, attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const applyShareMeta = ({ title, description, image, url }) => {
  document.title = `${title} — ${DEFAULT_TITLE}`;
  upsertMeta("meta[name='description']", "name", "description", description);
  upsertMeta("meta[property='og:type']", "property", "og:type", "article");
  upsertMeta("meta[property='og:title']", "property", "og:title", title);
  upsertMeta("meta[property='og:description']", "property", "og:description", description);
  upsertMeta("meta[property='og:image']", "property", "og:image", image);
  upsertMeta("meta[property='og:url']", "property", "og:url", url);
  upsertMeta("meta[name='twitter:card']", "name", "twitter:card", image ? "summary_large_image" : "summary");
  upsertMeta("meta[name='twitter:title']", "name", "twitter:title", title);
  upsertMeta("meta[name='twitter:description']", "name", "twitter:description", description);
  upsertMeta("meta[name='twitter:image']", "name", "twitter:image", image);
  let canonical = document.head.querySelector("link[rel='canonical']");
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
};

const PSA_ACTIVE = new Set(["Submitted to PSA", "Graded", "Returned from PSA"]);
const PSA_GRADED = new Set(["Graded", "Returned from PSA"]);

const SUBNAV = [
  { id: "overview", label: "Overview" },
  { id: "categories", label: "Categories" },
  { id: "gallery", label: "Gallery" },
  { id: "provenance", label: "Provenance" },
  { id: "graceland", label: "Graceland" },
  { id: "psa", label: "PSA Grading" },
];

/* ==================== Showcase sub-navigation ==================== */
/* BB-067A: Wrapped two-row navigation on mobile - no horizontal scroll */
const SubNav = ({ hide = [] }) => (
  <nav
    className="sticky top-20 z-30 bg-[hsl(42,25%,96%)]/95 backdrop-blur border-y border-[hsl(43,74%,52%,0.2)]"
    data-testid="showcase-subnav"
  >
    <div className="w-full max-w-7xl mx-auto px-3 md:px-12 lg:px-16">
      <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 md:gap-3 py-2.5">
        {SUBNAV.filter((s) => !hide.includes(s.id)).map((s) => (
          <button
            key={s.id}
            onClick={() => scrollToId(s.id)}
            style={{ minHeight: '44px' }}
            className="font-ui text-[11px] md:text-xs tracking-[0.08em] md:tracking-[0.15em] uppercase font-semibold text-[hsl(0,0%,40%)] hover:text-[hsl(355,76%,33%)] hover:bg-[hsl(355,76%,33%,0.08)] transition-colors px-2.5 md:px-4 py-2 inline-flex items-center justify-center rounded-sm focus:outline-none focus:ring-2 focus:ring-[hsl(355,76%,33%)] focus:ring-offset-1"
            data-testid={`subnav-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
    </div>
  </nav>
);

/* ==================== Landing page ==================== */
export const CollectionShowcasePage = () => {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/collection/settings"),
      api.get("/collection/categories"),
      api.get("/collection/items"),
      api.get("/collection/timeline"),
    ])
      .then(([s, c, i, t]) => {
        setSettings(s.data);
        setCategories(c.data.filter((cat) => cat.count > 0));
        setItems(i.data);
        setTimeline(t.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Scroll to a section when arriving with a hash (e.g. /collection-story#provenance)
  useEffect(() => {
    if (loading) return;
    const hash = typeof window !== "undefined" ? window.location.hash.replace("#", "") : "";
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [loading]);

  const categoryImage = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      if (!map[it.category] && it.images?.[0]?.url) map[it.category] = it.images[0].url;
    });
    return map;
  }, [items]);

  const filteredItems = useMemo(
    () => (activeCategory === "All" ? items : items.filter((i) => i.category === activeCategory)),
    [items, activeCategory]
  );

  const gracelandItems = useMemo(() => items.filter((i) => i.graceland_displayed), [items]);
  const psaItems = useMemo(() => items.filter((i) => PSA_ACTIVE.has(i.psa_status)), [items]);
  const psaGradedCount = useMemo(() => items.filter((i) => PSA_GRADED.has(i.psa_status)).length, [items]);

  const stats = [
    { label: "Documented Items", value: items.length },
    { label: "Provenance Events", value: timeline.length },
    { label: "PSA Certified", value: psaGradedCount },
    { label: "Graceland Displayed", value: gracelandItems.length },
  ];

  const selectCategory = (name) => {
    setActiveCategory(name);
    setTimeout(() => scrollToId("gallery"), 60);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-32 bg-[hsl(42,25%,96%)]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[hsl(0,0%,80%)] border-t-[hsl(355,76%,33%)]" />
      </div>
    );
  }

  const s = settings || {};

  return (
    <div className="reading-surface" data-testid="collection-showcase-page">
      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden border-b border-[hsl(43,74%,52%,0.2)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 md:pt-24 pb-16 md:pb-24">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <Reveal className="lg:col-span-7">
              <p className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-4">{s.hero_eyebrow}</p>
              <h1 className="font-display font-light tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-6xl text-[hsl(0,0%,10%)] mt-5 mb-6">
                {s.hero_title}
              </h1>
              <p className="text-base md:text-lg font-editorial text-[hsl(0,0%,40%)] leading-relaxed max-w-2xl">
                {s.hero_summary}
              </p>
              <div className="flex flex-wrap gap-4 mt-9">
                <button
                  onClick={() => scrollToId("gallery")}
                  className="inline-flex items-center gap-2 bg-[hsl(0,0%,10%)] text-white rounded-none px-7 py-3.5 font-ui text-sm tracking-wide hover:bg-[hsl(355,76%,33%)] transition-colors duration-300"
                  data-testid="hero-explore-btn"
                >
                  Explore the Collection <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => scrollToId("provenance")}
                  className="inline-flex items-center gap-2 bg-transparent border border-[hsl(0,0%,10%)] text-[hsl(0,0%,10%)] rounded-none px-7 py-3.5 font-ui text-sm tracking-wide hover:bg-[hsl(0,0%,90%)] transition-colors duration-300"
                  data-testid="hero-provenance-btn"
                >
                  View Provenance
                </button>
                <a
                  href={`${API}/collection/dossier.pdf`}
                  className="inline-flex items-center gap-2 bg-transparent border border-[hsl(0,0%,80%)] text-[hsl(0,0%,40%)] rounded-none px-7 py-3.5 font-ui text-sm tracking-wide hover:border-[hsl(0,0%,50%)] hover:text-[hsl(0,0%,10%)] transition-colors duration-300"
                  data-testid="hero-dossier-btn"
                >
                  Download Dossier
                </a>
                <Link
                  to="/archives"
                  className="inline-flex items-center gap-2 font-ui text-sm tracking-wide text-[hsl(355,76%,33%)] hover:underline px-2 py-3.5"
                  data-testid="hero-archives-btn"
                >
                  Archive Downloads
                </Link>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-5" delay={150}>
              <FramedImage
                src={s.hero_image}
                alt={s.hero_title}
                className="aspect-[4/5] p-2 md:p-3"
              />
              <p className="mt-3 font-ui text-xs uppercase tracking-[0.15em] text-[hsl(0,0%,50%)]">Plate 001 — Archival reference image</p>
            </Reveal>
          </div>
        </div>
      </section>

      <SubNav hide={[...(psaItems.length === 0 ? ["psa"] : []), ...(gracelandItems.length === 0 ? ["graceland"] : [])]} />

      {/* ---------- Overview ---------- */}
      <section id="overview" className="scroll-mt-32 border-b border-[hsl(43,74%,52%,0.2)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-4">
              <p className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-3">01 — About</p>
              <h2 className="font-display text-3xl sm:text-4xl font-normal tracking-tight text-[hsl(0,0%,10%)] mt-4">
                {s.overview_heading}
              </h2>
            </Reveal>
            <Reveal className="lg:col-span-8" delay={100}>
              <div className="space-y-5 text-[15px] md:text-base font-editorial text-[hsl(0,0%,40%)] leading-relaxed max-w-3xl">
                {(s.overview_body || "").split("\n\n").map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              {s.why_it_matters && (
                <div className="mt-8 border-l-2 border-[hsl(355,76%,33%)] pl-6 py-1 max-w-3xl">
                  <p className="font-ui text-xs uppercase tracking-[0.2em] text-[hsl(355,76%,33%)] mb-2">Why It Matters</p>
                  <p className="font-editorial text-lg md:text-xl text-[hsl(0,0%,10%)] leading-relaxed">
                    {s.why_it_matters}
                  </p>
                </div>
              )}
            </Reveal>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#D6D3C8] border border-[#D6D3C8] mt-16">
            {stats.map((st) => (
              <div key={st.label} className="bg-[#F5F4F0] p-6 md:p-8 text-center">
                <div className="showcase-serif text-4xl md:text-5xl font-light text-[#8C271E] tabular-nums">
                  {st.value}
                </div>
                <div className="showcase-label mt-2">{st.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Categories ---------- */}
      <section id="categories" className="scroll-mt-32 border-b border-[#D6D3C8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <SectionLabel className="text-[#8C271E]">02 — Categories</SectionLabel>
            <h2 className="showcase-serif text-3xl sm:text-4xl font-normal tracking-tight mt-4 mb-12">
              What the Collection Contains
            </h2>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((cat, idx) => (
              <Reveal key={cat.name} delay={Math.min(idx * 60, 300)}>
                <CategoryCard
                  category={cat}
                  image={categoryImage[cat.name]}
                  onSelect={() => selectCategory(cat.name)}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Gallery ---------- */}
      <section id="gallery" className="scroll-mt-32 border-b border-[#D6D3C8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <SectionLabel className="text-[#8C271E]">03 — Gallery</SectionLabel>
            <h2 className="showcase-serif text-3xl sm:text-4xl font-normal tracking-tight mt-4 mb-8">
              The Collection
            </h2>
          </Reveal>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-2 mb-12">
            {["All", ...categories.map((c) => c.name)].map((name) => (
              <button
                key={name}
                onClick={() => setActiveCategory(name)}
                className={`rounded-none border px-4 py-2 text-xs tracking-[0.1em] uppercase font-semibold transition-colors ${
                  activeCategory === name
                    ? "bg-[#1A1918] text-white border-[#1A1918]"
                    : "bg-transparent text-[#5C5A56] border-[#D6D3C8] hover:border-[#5C5A56]"
                }`}
                data-testid={`gallery-filter-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {name}
              </button>
            ))}
          </div>

          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {filteredItems.map((item, idx) => (
              <Reveal key={item.id} delay={Math.min((idx % 3) * 80, 240)}>
                <ItemCard item={item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Provenance timeline ---------- */}
      <section id="provenance" className="scroll-mt-32 border-b border-[#D6D3C8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-4">
              <SectionLabel className="text-[#8C271E]">04 — Provenance</SectionLabel>
              <h2 className="showcase-serif text-3xl sm:text-4xl font-normal tracking-tight mt-4">
                A Continuous Documented Chain
              </h2>
              <p className="mt-4 text-[15px] text-[#5C5A56] leading-relaxed">
                From original production through Graceland display, return, PSA certification,
                and current archival custody.
              </p>
            </Reveal>
            <div className="lg:col-span-8">
              <ProvenanceTimeline events={timeline} />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Graceland ---------- */}
      <section id="graceland" className="scroll-mt-32 border-b border-[#D6D3C8] bg-[#EAE8E0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="flex items-center gap-3">
              <Landmark className="w-5 h-5 text-[#8C271E]" />
              <SectionLabel className="text-[#8C271E]">05 — Graceland Loan & Display</SectionLabel>
            </div>
            <h2 className="showcase-serif text-3xl sm:text-4xl font-normal tracking-tight mt-4 mb-4">
              Exhibited at Graceland
            </h2>
            <p className="text-[15px] md:text-base text-[#5C5A56] leading-relaxed max-w-3xl mb-12">
              {s.graceland_intro}
            </p>
          </Reveal>
          {gracelandItems.length > 0 ? (
            <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
              {gracelandItems.map((item, idx) => (
                <Reveal key={item.id} delay={Math.min(idx * 80, 240)}>
                  <ItemCard item={item} />
                </Reveal>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#5C5A56]">Graceland display records will appear here.</p>
          )}
        </div>
      </section>

      {/* ---------- PSA grading ---------- */}
      {psaItems.length > 0 && (
      <section id="psa" className="scroll-mt-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
          <Reveal>
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-[#8C271E]" />
              <SectionLabel className="text-[#8C271E]">06 — PSA Grading</SectionLabel>
            </div>
            <h2 className="showcase-serif text-3xl sm:text-4xl font-normal tracking-tight mt-4 mb-4">
              Independently Authenticated
            </h2>
            <p className="text-[15px] md:text-base text-[#5C5A56] leading-relaxed max-w-3xl mb-12">
              {s.psa_intro}
            </p>
          </Reveal>
          <div className="space-y-px bg-[#D6D3C8] border border-[#D6D3C8]">
            {psaItems.map((item) => (
              <Link
                key={item.id}
                to={`/collection/item/${item.slug}`}
                className="group flex items-center gap-5 bg-[#F5F4F0] p-5 md:p-6 hover:bg-white transition-colors"
                data-testid={`psa-row-${item.slug}`}
              >
                <FramedImage
                  src={item.images?.find((im) => im.kind === "psa-slab")?.url || item.images?.[0]?.url}
                  alt={item.title}
                  className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <SectionLabel className="text-[#8C271E]">{item.category}</SectionLabel>
                  <h3 className="showcase-serif text-lg md:text-xl text-[#1A1918] group-hover:text-[#8C271E] transition-colors truncate">
                    {item.title}
                  </h3>
                  {item.psa_cert_number && (
                    <p className="text-xs text-[#5C5A56] mt-1">Cert. No. {item.psa_cert_number}</p>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="showcase-serif text-base md:text-lg text-[#1A1918]">
                    {item.psa_grade || item.psa_status}
                  </div>
                  <div className="showcase-label mt-1">{item.psa_grade ? "PSA Grade" : "PSA Status"}</div>
                </div>
                <ChevronRight className="w-5 h-5 text-[#9A9790] flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>
      )}
    </div>
  );
};

/* ==================== Item detail page (split-screen) ==================== */
export const CollectionItemDetailPage = () => {
  const { slug } = useParams();
  const { openViewer } = useMediaViewer();
  const [data, setData] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/collection/items/${slug}`)
      .then((res) => {
        setData(res.data);
        setActiveImg(0);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    const it = data?.item;
    if (!it) return;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const image = (it.images || []).find((im) => im.kind === "primary")?.url || it.images?.[0]?.url;
    applyShareMeta({
      title: it.title,
      description: it.short_description || it.provenance_summary || "",
      image,
      url: `${origin}/collection/item/${it.slug}`,
    });
    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [data]);

  if (loading) {
    return (
      <div className="flex justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8C271E]" />
      </div>
    );
  }

  if (!data?.item) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <p className="text-[#5C5A56] mb-4">Collection item not found.</p>
        <Link to="/collection-story" className="text-[#8C271E] hover:underline">
          ← Back to the Collection
        </Link>
      </div>
    );
  }

  const { item, related, citations, research_docs = [], related_artifacts = [], provenance_events = [] } = data;
  const images = item.images || [];
  const current = images[activeImg];
  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const shareUrl = `${origin}/api/share/collection/${item.slug}`;
  const qrUrl = `${API}/collection/items/${item.slug}/qr.png`;
  const pdfUrl = `${API}/collection/items/${item.slug}/provenance.pdf`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Shareable link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      window.prompt("Copy this shareable link:", shareUrl);
    }
  };

  const openZoom = () => {
    if (current?.url) {
      openViewer({ type: "image", source: current.url, caption: current.caption || item.title });
    }
  };

  return (
    <div className="bg-[#F5F4F0] text-[#1A1918]" data-testid="collection-item-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-14">
        <Link
          to="/collection-story"
          className="inline-flex items-center gap-2 text-xs tracking-[0.15em] uppercase font-semibold text-[#5C5A56] hover:text-[#8C271E] transition-colors mb-8"
          data-testid="detail-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to the Collection
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left: sticky image viewer */}
          <div className="lg:col-span-7 min-w-0">
            <div className="lg:sticky lg:top-24">
              <button
                onClick={openZoom}
                className="group relative block w-full"
                data-testid="detail-image-zoom"
                aria-label="Zoom image"
              >
                <FramedImage
                  src={current?.url}
                  alt={current?.caption || item.title}
                  className="aspect-[4/3] p-2 md:p-3"
                  pending={!!current?.is_placeholder}
                />
                <span className="absolute top-5 right-5 bg-white/90 border border-[#D6D3C8] rounded-none p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ZoomIn className="w-4 h-4 text-[#1A1918]" />
                </span>
              </button>
              {current?.caption && (
                <p className="mt-3 showcase-label">{current.caption}</p>
              )}
              {images.length > 1 && (
                <div className="mt-4 grid grid-cols-4 sm:grid-cols-5 gap-3">
                  {images.map((im, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`showcase-frame overflow-hidden aspect-square ${
                        i === activeImg ? "ring-2 ring-[#8C271E]" : ""
                      }`}
                      data-testid={`detail-thumb-${i}`}
                    >
                      <img
                        src={im.url}
                        alt={im.caption || `${item.title} ${i + 1}`}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: scholarly content */}
          <div className="lg:col-span-5 min-w-0">
            <div className="flex items-center justify-between gap-3 mb-3">
              <SectionLabel className="text-[#8C271E]">{item.category}</SectionLabel>
              <ProvenanceStatusBadge status={item.provenance_status} />
            </div>
            <h1 className="showcase-serif text-3xl sm:text-4xl font-light tracking-tight leading-tight break-words">
              {item.title}
            </h1>
            {item.short_description && (
              <p className="mt-5 text-base text-[#5C5A56] leading-relaxed">{item.short_description}</p>
            )}

            {/* Documentation status */}
            <div className="mt-4 flex items-center gap-2" data-testid="documentation-status">
              <SectionLabel className="text-[#1A1918]">Documentation Status</SectionLabel>
              <ProvenanceStatusBadge status={item.provenance_status} />
            </div>

            {/* Share actions */}
            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="share-bar">
              <button
                onClick={handleCopy}
                className="inline-flex items-center gap-2 bg-transparent border border-[#1A1918] text-[#1A1918] rounded-none px-4 py-2.5 text-xs tracking-[0.1em] uppercase font-semibold hover:bg-[#EAE8E0] transition-colors"
                data-testid="copy-share-link-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                {copied ? "Copied" : "Copy Link"}
              </button>
              <a
                href={qrUrl}
                download={`${item.slug}-qr.png`}
                className="inline-flex items-center gap-2 bg-transparent border border-[#D6D3C8] text-[#5C5A56] rounded-none px-4 py-2.5 text-xs tracking-[0.1em] uppercase font-semibold hover:border-[#5C5A56] hover:text-[#1A1918] transition-colors"
                data-testid="download-qr-btn"
              >
                <QrCode className="w-4 h-4" /> QR Code
              </a>
              <a
                href={pdfUrl}
                className="inline-flex items-center gap-2 bg-[#1A1918] text-white rounded-none px-4 py-2.5 text-xs tracking-[0.1em] uppercase font-semibold hover:bg-[#8C271E] transition-colors"
                data-testid="download-provenance-pdf-btn"
              >
                <FileDown className="w-4 h-4" /> Provenance PDF
              </a>
              <a
                href={`${API}/collection/dossier.pdf`}
                className="inline-flex items-center gap-2 bg-transparent border border-[#D6D3C8] text-[#5C5A56] rounded-none px-4 py-2.5 text-xs tracking-[0.1em] uppercase font-semibold hover:border-[#5C5A56] hover:text-[#1A1918] transition-colors"
                data-testid="download-dossier-btn"
              >
                <FileDown className="w-4 h-4" /> Dossier
              </a>
            </div>

            <div className="mt-8 space-y-8">
              {item.historical_context && (
                <DetailSection icon={ScrollText} title="Historical Context">
                  <p>{item.historical_context}</p>
                </DetailSection>
              )}

              {item.provenance_summary && (
                <DetailSection title="Provenance Summary">
                  <p>{item.provenance_summary}</p>
                </DetailSection>
              )}

              {item.graceland_displayed && (
                <DetailSection icon={Landmark} title="Public Display Record">
                  <div className="border border-[#D6D3C8] bg-white p-4">
                    <SectionLabel className="text-[#8C271E]">Graceland Loan &amp; Display</SectionLabel>
                    <p className="mt-2">{item.graceland_details || "This item was displayed to the public at Graceland."}</p>
                  </div>
                </DetailSection>
              )}

              {PSA_ACTIVE.has(item.psa_status) && (
                <DetailSection icon={ShieldCheck} title="Certification Record">
                  <div className="border border-[#D6D3C8] bg-white p-4">
                    <SectionLabel className="text-[#8C271E]">PSA Authentication</SectionLabel>
                    <dl className="mt-3 border border-[#D6D3C8] divide-y divide-[#D6D3C8]">
                      <PsaRow label="Status" value={item.psa_status} />
                      {item.psa_submission_date && (
                        <PsaRow label="Submitted" value={item.psa_submission_date} />
                      )}
                      {item.psa_return_date && (
                        <PsaRow label="Returned" value={item.psa_return_date} />
                      )}
                      {item.psa_grade && (
                        <PsaRow label="Grade" value={item.psa_grade} />
                      )}
                      {item.psa_cert_number && (
                        <PsaRow label="Certification No." value={item.psa_cert_number} />
                      )}
                    </dl>
                    {(() => {
                      const slab = item.images?.find((im) => im.kind === "psa-slab");
                      return slab ? (
                        <div className="mt-3">
                          <FramedImage src={slab.url} alt={slab.caption || "PSA slab"} className="aspect-[4/3]" pending={!!slab.is_placeholder} />
                        </div>
                      ) : null;
                    })()}
                    {item.psa_notes && <p className="mt-3">{item.psa_notes}</p>}
                  </div>
                </DetailSection>
              )}

              {item.references && (
                <DetailSection title="References">
                  <p>{item.references}</p>
                </DetailSection>
              )}

              {citations?.length > 0 && (
                <DetailSection title="Primary Sources">
                  <CitationList citations={citations} />
                </DetailSection>
              )}

              {related?.length > 0 && (
                <div>
                  <SectionLabel className="text-[#8C271E]">Related Items</SectionLabel>
                  <div className="mt-4 space-y-px bg-[#D6D3C8] border border-[#D6D3C8]">
                    {related.map((r) => (
                      <Link
                        key={r.id}
                        to={`/collection/item/${r.slug}`}
                        className="group flex items-center gap-4 bg-[#F5F4F0] p-3 hover:bg-white transition-colors"
                        data-testid={`related-item-${r.slug}`}
                      >
                        <FramedImage
                          src={r.images?.[0]?.url}
                          alt={r.title}
                          className="w-14 h-14 flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <p className="showcase-serif text-[15px] text-[#1A1918] group-hover:text-[#8C271E] transition-colors truncate">
                            {r.title}
                          </p>
                          <p className="text-xs text-[#5C5A56] truncate">{r.category}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#9A9790] ml-auto flex-shrink-0" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {related_artifacts?.length > 0 && (
                <div>
                  <SectionLabel className="text-[#8C271E]">Related Artifacts</SectionLabel>
                  <div className="mt-4 space-y-px bg-[#D6D3C8] border border-[#D6D3C8]">
                    {related_artifacts.map((a) => (
                      <Link key={a.id} to={`/artifacts/${a.slug}`} className="group flex items-center justify-between gap-3 bg-[#F5F4F0] p-3 hover:bg-white transition-colors" data-testid={`related-artifact-${a.slug}`}>
                        <div className="min-w-0">
                          <p className="showcase-serif text-[15px] text-[#1A1918] group-hover:text-[#8C271E] transition-colors truncate">{a.name}</p>
                          {a.category && <p className="text-xs text-[#5C5A56] truncate">{a.category}</p>}
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#9A9790] flex-shrink-0" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {research_docs?.length > 0 && (
                <div>
                  <SectionLabel className="text-[#8C271E]">Research Documents</SectionLabel>
                  <div className="mt-4 space-y-px bg-[#D6D3C8] border border-[#D6D3C8]">
                    {research_docs.map((r) => (
                      <Link key={r.id} to={`/research/${r.slug}`} className="group flex items-center justify-between gap-3 bg-[#F5F4F0] p-3 hover:bg-white transition-colors" data-testid={`research-doc-${r.slug}`}>
                        <div className="min-w-0">
                          <p className="showcase-serif text-[15px] text-[#1A1918] group-hover:text-[#8C271E] transition-colors truncate">{r.title}</p>
                          <p className="text-xs text-[#5C5A56] truncate">{[r.doc_type, r.date].filter(Boolean).join(" · ")}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#9A9790] flex-shrink-0" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {provenance_events?.length > 0 && (
                <div>
                  <SectionLabel className="text-[#8C271E]">Timeline</SectionLabel>
                  <div className="mt-4 space-y-3">
                    {provenance_events.map((ev) => (
                      <div key={ev.id} className="border-l-2 border-[#8C271E]/40 pl-3" data-testid={`timeline-link-${ev.id}`}>
                        <div className="text-[11px] tracking-[0.15em] uppercase text-[#8C271E]">{ev.date_label}</div>
                        <div className="showcase-serif text-[15px] text-[#1A1918]">{ev.title}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-2">
                <CiteThis title={item.title} date={item.updated_at} url={pageUrl} />
              </div>
            </div>
          </div>
        </div>

        {item.article_markdown && (
          <section className="mt-16 pt-12 border-t border-[#D6D3C8]" data-testid="historical-narrative">
            <div className="max-w-3xl">
              <SectionLabel className="text-[#8C271E]">Historical Narrative</SectionLabel>
              <h2 className="showcase-serif text-3xl sm:text-4xl font-light tracking-tight mt-3 mb-8">
                {item.title}
              </h2>
              <ArticleRenderer markdown={item.article_markdown} citations={citations} />
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

const DetailSection = ({ icon: Icon, title, children }) => (
  <section>
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-4 h-4 text-[#8C271E]" />}
      <SectionLabel className="text-[#1A1918]">{title}</SectionLabel>
    </div>
    <div className="text-[15px] text-[#5C5A56] leading-relaxed space-y-3">{children}</div>
  </section>
);

const PsaRow = ({ label, value }) => (
  <div className="flex items-center justify-between px-4 py-3">
    <span className="showcase-label">{label}</span>
    <span className="showcase-serif text-[#1A1918]">{value}</span>
  </div>
);

/* ==================== Public Collection Dossier page ==================== */
export const CollectionDossierPage = () => {
  const [info, setInfo] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/collection/dossier/info"),
      api.get("/collection/settings"),
    ])
      .then(([i, s]) => { setInfo(i.data); setSettings(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const latest = info?.latest;

  return (
    <div className="bg-[#F5F4F0] text-[#1A1918] min-h-[70vh]" data-testid="collection-dossier-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <SectionLabel className="text-[#8C271E]">Institutional Package</SectionLabel>
        <h1 className="showcase-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl mt-4 mb-6">
          The Collection Dossier
        </h1>
        <p className="text-base md:text-lg text-[#5C5A56] leading-relaxed max-w-3xl">
          A museum-quality provenance &amp; historical documentation package for the Elvis Aloha from Hawaii 1973
          Collection, generated directly from the living archive. Suitable for institutions, museums, researchers,
          and insurers.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-10 items-start">
          <FramedImage src={settings.hero_image} alt="Collection Dossier" className="aspect-[4/3] p-2 md:p-3" />
          <div>
            {loading ? (
              <p className="text-[#5C5A56]">Loading…</p>
            ) : latest ? (
              <>
                <dl className="border border-[#D6D3C8] divide-y divide-[#D6D3C8] bg-white">
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="showcase-label">Current Version</span>
                    <span className="showcase-serif text-[#1A1918]">v{latest.version}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="showcase-label">Last Generated</span>
                    <span className="text-sm text-[#1A1918]">{new Date(latest.generated_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3 gap-4">
                    <span className="showcase-label flex-shrink-0">Archive Hash</span>
                    <span className="text-[11px] font-mono text-[#5C5A56] truncate">{latest.content_hash?.slice(0, 24)}…</span>
                  </div>
                </dl>
                <a
                  href={`${API}/collection/dossier.pdf`}
                  className="mt-6 inline-flex items-center gap-2 bg-[#1A1918] text-white rounded-none px-7 py-3.5 text-sm tracking-wide hover:bg-[#8C271E] transition-colors duration-300"
                  data-testid="download-dossier-btn"
                >
                  <FileDown className="w-4 h-4" /> Download Collection Dossier (PDF)
                </a>
                <p className="mt-4 text-xs text-[#5C5A56]">
                  This document is automatically generated from the Elvis Aloha 1973 Collection Archive and reflects
                  the archive at the time of generation.
                </p>
              </>
            ) : (
              <p className="text-[#5C5A56]">The dossier is being prepared. Please check back shortly.</p>
            )}
          </div>
        </div>

        <div className="mt-12">
          <Link to="/collection-story" className="text-sm text-[#8C271E] hover:underline">← Back to the Collection</Link>
        </div>
      </div>
    </div>
  );
};

export default CollectionShowcasePage;
